import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { AiDisclosure } from "@/components/AiDisclosure";
import { SwishPricing } from "@/components/SwishPricing";
import { SwishQuickPay } from "@/components/SwishQuickPay";
import { ACCENT_PRESETS } from "@/lib/cvStyle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, HelpCircle,
  XCircle, Sparkles, FileText, ClipboardList, CreditCard, Lightbulb, Briefcase, Heart, Mail, Lock, ExternalLink,
} from "lucide-react";

const STEPS = ["Jobbannons", "AI-analys", "Din matchning", "Skapa CV"];

const TEMPLATES = [
  { id: "klassisk", name: "Klassisk", desc: "Ren och traditionell." },
  { id: "modern", name: "Modern", desc: "Modern men ATS-vänlig." },
  { id: "kompakt", name: "Kompakt", desc: "För lång erfarenhet." },
  { id: "grafisk", name: "Grafisk", desc: "Färgaccent för mer intryck." },
];

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8 flex-wrap">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${i <= step ? "bg-white/20" : "bg-white"}`}>{i + 1}</span>
            {s}
          </div>
          {i < STEPS.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      ))}
    </div>
  );
}

function Chips({ items, color }) {
  if (!items || items.length === 0) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((x, i) => <Badge key={i} variant="secondary" className={`rounded-full font-normal ${color || ""}`}>{x}</Badge>)}
    </div>
  );
}

export default function SkapaCV() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ company: "", title: "", url: "", job_ad_text: "" });
  const [appId, setAppId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [matching, setMatching] = useState(null);
  const [template, setTemplate] = useState("modern");
  const [accentColor, setAccentColor] = useState("#2E5C4B");
  const [layout, setLayout] = useState("standard");
  const [cvType, setCvType] = useState("standard");
  const [hasExperience, setHasExperience] = useState(true);
  const [cvTypeTouched, setCvTypeTouched] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null); // {id}
  const [highlights, setHighlights] = useState(null);
  const [hlLoading, setHlLoading] = useState(false);

  // Complement dialog
  const [comp, setComp] = useState(null); // {item, question}
  const [compAnswer, setCompAnswer] = useState("yes");
  const [compText, setCompText] = useState("");

  useEffect(() => {
    api.get("/profile").then((r) => {
      const exp = (r.data.work_experiences || []).length > 0;
      setHasExperience(exp);
      if (!cvTypeTouched) setCvType(exp ? "standard" : "character");
    }).catch(() => {});
    // Förifyll från Vägledning ("Skapa CV för detta jobb")
    try {
      const raw = localStorage.getItem("hamn_prefill_job");
      if (raw) {
        const pf = JSON.parse(raw);
        setForm((f) => ({ ...f, ...pf }));
        localStorage.removeItem("hamn_prefill_job");
        toast.success("Jobbannonsen hämtades från Vägledning");
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectCvType = (t) => { setCvType(t); setCvTypeTouched(true); };

  const goAnalyze = async () => {
    if (!form.job_ad_text.trim()) { toast.error("Klistra in jobbannonsen först"); return; }
    setLoading(true);
    try {
      const { data: appData } = await api.post("/applications", form);
      setAppId(appData.id);
      const { data } = await api.post(`/applications/${appData.id}/analyze`);
      setAnalysis(data.analysis);
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const goMatch = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/applications/${appId}/match`);
      setMatching(data.matching);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const goGenerate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/applications/${appId}/generate-cv`, { template, cv_type: cvType, accent_color: accentColor, layout });
      toast.success(data.locked ? "CV skapat och sparat!" : "CV skapat!");
      setResult({ id: data.id, locked: !!data.locked, apply_url: data.apply_url || "" });
      setHlLoading(true);
      api.post(`/cvs/${data.id}/highlights`)
        .then((r) => setHighlights(r.data.highlights))
        .catch(() => {})
        .finally(() => setHlLoading(false));
    } catch (e) {
      if (e.response?.status === 402) {
        setPaywall(formatApiError(e.response?.data?.detail));
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setLoading(false); }
  };

  const redeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/redeem", { code: redeemCode });
      toast.success(data.message || "Kod inlöst");
      setPaywall(null); setRedeemCode("");
      await goGenerate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRedeeming(false); }
  };

  const saveComplement = async () => {
    if (compAnswer !== "yes" || !compText.trim()) { setComp(null); return; }
    try {
      await api.post("/profile/complement", { item: comp.item, description: compText });
      toast.success("Sparat i din Masterprofil");
      setComp(null); setCompText(""); setCompAnswer("yes");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Skapa CV för ett jobb" description="Klistra in jobbannonsen. AI analyserar kraven, matchar mot din profil och skapar ett ATS-vänligt CV – baserat enbart på dina verkliga uppgifter." />
      <Stepper step={step} />

      {/* Step 0: job ad */}
      {step === 0 && (
        <Card className="rounded-2xl border-border p-6 md:p-8 stagger-in">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="company">Företagsnamn</Label>
              <Input data-testid="job-company-input" id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="T.ex. Volvo" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Tjänstens namn</Label>
              <Input data-testid="job-title-input" id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="T.ex. Lagerkoordinator" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="url">Länk till annonsen (valfritt)</Label>
              <Input data-testid="job-url-input" id="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="ad">Klistra in hela jobbannonsen</Label>
              <Textarea data-testid="job-ad-textarea" id="ad" rows={12} value={form.job_ad_text} onChange={(e) => setForm({ ...form, job_ad_text: e.target.value })} placeholder="Klistra in annonstexten här…" />
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button data-testid="analyze-btn" className="rounded-full" onClick={goAnalyze} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Analysera annonsen
            </Button>
          </div>
        </Card>
      )}

      {/* Step 1: analysis */}
      {step === 1 && analysis && (
        <div className="stagger-in space-y-5">
          <AiDisclosure text={<><strong className="text-foreground">AI-genererad analys.</strong> AI:n tolkar jobbannonsen – granska gärna själv. Inga beslut fattas åt dig.</>} />
          <Card className="rounded-2xl border-border p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4"><ClipboardList className="h-5 w-5 text-primary" /><h3 className="font-head text-xl font-semibold">{analysis.job_title || form.title || "Jobbanalys"}</h3></div>
            <div className="grid gap-6 md:grid-cols-2">
              <div><div className="text-sm font-semibold mb-2 text-[hsl(var(--destructive))]">Måste finnas</div><Chips items={analysis.required_skills} /></div>
              <div><div className="text-sm font-semibold mb-2 text-primary">Meriterande</div><Chips items={analysis.meriting_skills} /></div>
              <div><div className="text-sm font-semibold mb-2 text-accent">Kan sannolikt läras</div><Chips items={analysis.learnable_on_job} /></div>
              <div><div className="text-sm font-semibold mb-2">System och verktyg</div><Chips items={analysis.systems_tools} /></div>
              <div><div className="text-sm font-semibold mb-2">Huvudsakliga arbetsuppgifter</div><Chips items={analysis.main_tasks} /></div>
              <div><div className="text-sm font-semibold mb-2">Egenskaper</div><Chips items={analysis.personal_traits} /></div>
              <div><div className="text-sm font-semibold mb-2">Utbildningskrav</div><Chips items={analysis.education_requirements} /></div>
              <div><div className="text-sm font-semibold mb-2">Nyckelord</div><Chips items={analysis.keywords} /></div>
            </div>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" className="rounded-full" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-2" /> Tillbaka</Button>
            <Button data-testid="match-btn" className="rounded-full" onClick={goMatch} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />} Se din matchning
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: matching */}
      {step === 2 && matching && (
        <div className="stagger-in space-y-5">
          <Card className="rounded-2xl border-border p-6 md:p-8 bg-primary text-primary-foreground">
            <div className="flex items-end justify-between flex-wrap gap-2">
              <div>
                <div className="text-primary-foreground/80 text-sm">Din matchning mot tjänsten</div>
                <div className="font-head text-4xl font-bold">{matching.match_score ?? 0}%</div>
              </div>
              <p className="text-sm text-primary-foreground/85 max-w-md">{matching.summary}</p>
            </div>
          </Card>

          <div className="grid gap-5 md:grid-cols-2">
            <MatchCard title="Stark match" icon={CheckCircle2} color="text-[hsl(var(--success))]" items={matching.strong_match} keyField="evidence" testid="match-strong" />
            <MatchCard title="Delvis match" icon={AlertCircle} color="text-[hsl(var(--warning))]" items={matching.partial_match} keyField="evidence" testid="match-partial" />
            <MatchCard title="Saknas i din profil" icon={XCircle} color="text-destructive" items={matching.missing} keyField="reason" testid="match-missing" />
            <Card className="rounded-2xl border-border p-6" data-testid="match-complement">
              <div className="flex items-center gap-2 mb-3 font-semibold text-primary"><HelpCircle className="h-5 w-5" /> Kan vara värt att komplettera</div>
              <div className="space-y-3">
                {(matching.worth_complementing || []).length === 0 && <p className="text-sm text-muted-foreground">Inget att komplettera.</p>}
                {(matching.worth_complementing || []).map((m, i) => (
                  <div key={i} className="rounded-lg bg-muted p-3">
                    <div className="font-medium text-sm">{m.item}</div>
                    <p className="text-sm text-muted-foreground mt-1">{m.question}</p>
                    <Button data-testid={`complement-btn-${i}`} size="sm" variant="outline" className="rounded-full mt-2" onClick={() => { setComp(m); setCompAnswer("yes"); setCompText(""); }}>
                      Komplettera min profil
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
            AI föreslår aldrig att du ska skriva att du kan något du inte kan. Luckor markeras – de läggs aldrig till i CV:t.
          </p>

          <div className="flex justify-between">
            <Button variant="outline" className="rounded-full" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" /> Tillbaka</Button>
            <Button data-testid="to-generate-btn" className="rounded-full" onClick={() => setStep(3)}><ArrowRight className="h-4 w-4 mr-2" /> Fortsätt till CV</Button>
          </div>
        </div>
      )}

      {/* Step 3: template + generate */}
      {step === 3 && !result && (
        <div className="stagger-in space-y-5">
          <Card className="rounded-2xl border-border p-6 md:p-8" data-testid="cv-type-card">
            <div className="flex items-center gap-2 mb-1"><Sparkles className="h-5 w-5 text-primary" /><h3 className="font-head text-xl font-semibold">Välj typ av CV</h3></div>
            <p className="text-sm text-muted-foreground mb-4">Har du lite eller ingen arbetslivserfarenhet? Välj ett karaktärs-CV som presenterar dig utifrån dina egenskaper, din utbildning och dina intressen i stället för jobbhistorik.</p>
            {!hasExperience && (
              <div className="flex items-start gap-2 rounded-xl bg-accent/10 border border-accent/25 px-4 py-3 mb-4 text-sm" data-testid="cv-type-suggestion">
                <Lightbulb className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                <span>Vi hittade ingen arbetslivserfarenhet i din profil – därför föreslår vi ett <strong>karaktärs-CV</strong>. Du kan byta om du vill.</span>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <button
                data-testid="cv-type-standard"
                onClick={() => selectCvType("standard")}
                className={`text-left rounded-xl border-2 p-5 transition-colors ${cvType === "standard" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              >
                <div className="flex items-center gap-2 font-head font-semibold"><Briefcase className="h-4 w-4 text-primary" /> Vanligt CV</div>
                <div className="text-sm text-muted-foreground mt-1">Bygger på din arbetslivserfarenhet och kompetens, riktat mot tjänsten.</div>
              </button>
              <button
                data-testid="cv-type-character"
                onClick={() => selectCvType("character")}
                className={`text-left rounded-xl border-2 p-5 transition-colors ${cvType === "character" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              >
                <div className="flex items-center gap-2 font-head font-semibold"><Heart className="h-4 w-4 text-primary" /> Karaktärs-CV</div>
                <div className="text-sm text-muted-foreground mt-1">Presenterar dig utifrån karaktär, styrkor, utbildning och intressen – för dig utan lång jobbhistorik.</div>
              </button>
            </div>
          </Card>

          <Card className="rounded-2xl border-border p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-primary" /><h3 className="font-head text-xl font-semibold">Välj CV-mall</h3></div>
            <div className="grid gap-4 sm:grid-cols-4">
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  data-testid={`template-${tpl.id}`}
                  onClick={() => setTemplate(tpl.id)}
                  className={`text-left rounded-xl border-2 p-5 transition-colors ${template === tpl.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                >
                  <div className="font-head font-semibold">{tpl.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">{tpl.desc}</div>
                </button>
              ))}
            </div>

            <div className="grid gap-6 sm:grid-cols-2 mt-6">
              <div className="space-y-2">
                <Label>Layout</Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setLayout("standard")} data-testid="skapa-layout-standard"
                    className={`flex-1 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${layout === "standard" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>Enkel kolumn</button>
                  <button type="button" onClick={() => setLayout("sidebar")} data-testid="skapa-layout-sidebar"
                    className={`flex-1 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${layout === "sidebar" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>Sidokolumn</button>
                </div>
                <p className="text-xs text-muted-foreground">Sidokolumn lägger kompetenser och kontakt i en färgad spalt.</p>
              </div>
              <div className="space-y-2">
                <Label>Accentfärg</Label>
                <div className="flex flex-wrap items-center gap-2.5" data-testid="skapa-accent-picker">
                  {ACCENT_PRESETS.map((col) => {
                    const active = accentColor.toUpperCase() === col.hex.toUpperCase();
                    return (
                      <button key={col.hex} type="button" title={col.name} aria-label={col.name}
                        onClick={() => setAccentColor(col.hex)} data-testid={`skapa-accent-${col.hex.replace('#','')}`}
                        className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${active ? "ring-2 ring-offset-2 ring-foreground scale-110" : ""}`}
                        style={{ backgroundColor: col.hex }} />
                    );
                  })}
                  <label className="relative h-8 w-8 rounded-full overflow-hidden ring-1 ring-border cursor-pointer inline-flex items-center justify-center bg-gradient-to-br from-pink-400 via-amber-300 to-sky-400" title="Egen färg">
                    <span className="text-white text-lg leading-none drop-shadow">+</span>
                    <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer" data-testid="skapa-accent-custom" />
                  </label>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" className="rounded-full" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-2" /> Tillbaka</Button>
            <Button data-testid="generate-cv-btn" size="lg" className="rounded-full" onClick={goGenerate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Skapa mitt CV
            </Button>
          </div>
        </div>
      )}

      {/* Complement dialog */}
      {result && (
        <div className="stagger-in space-y-5" data-testid="cv-result-view">
          <Card className="rounded-2xl border-border p-6 md:p-8 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2 font-head text-xl font-bold"><CheckCircle2 className="h-5 w-5" /> Ditt CV är skapat!</div>
            <p className="text-sm text-primary-foreground/85 mt-2">Nedan har AI tagit fram det som fångar en rekryterares intresse – helt utifrån dina verkliga uppgifter. Öppna CV:t i editorn för att finjustera och exportera.</p>
          </Card>

          {result.locked && (
            <>
              <Card className="rounded-2xl border-amber-300 bg-amber-50 p-5 md:p-6" data-testid="cv-locked-notice">
                <div className="flex items-center gap-2 font-head font-semibold text-amber-900"><Lock className="h-5 w-5" /> CV:t är sparat – lås upp för nedladdning</div>
                <p className="text-sm text-amber-900/80 mt-2">
                  Ditt CV är sparat och du kan öppna och redigera det när du vill. För att <strong>ladda ner</strong> det
                  som PDF eller Word: betala nedan med kort, Apple Pay, Google Pay, Link eller Klarna – då låses det upp
                  automatiskt direkt. Ditt arbete finns kvar under <strong>Mina CV:n</strong> under tiden.
                </p>
              </Card>
              <SwishQuickPay />
              <details className="rounded-xl border border-border p-4" data-testid="more-packages">
                <summary className="cursor-pointer text-sm font-medium">Vill du köpa fler CV på en gång?</summary>
                <div className="mt-4"><SwishPricing audience="candidate" /></div>
              </details>
            </>
          )}

          <AiDisclosure text={<><strong className="text-foreground">AI-genererat CV-utkast.</strong> Skapat utifrån dina egna uppgifter. Granska och redigera innan du använder det – du bestämmer alltid slutresultatet. Den exporterade filen märks som AI-genererad i metadata (EU:s AI-förordning, art. 50).</>} />

          <Card className="rounded-2xl border-primary/25 bg-primary/[0.03] p-6 md:p-8" data-testid="result-highlights-card">
            <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-5 w-5 text-primary" /><h3 className="font-head text-lg font-semibold">Det här fångar läsaren</h3></div>
            {hlLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Tar fram blickfång…</div>}
            {highlights && (
              <div className="space-y-4" data-testid="result-highlights-content">
                {highlights.hook && (
                  <div className="rounded-xl bg-white border border-border p-4">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Föreslagen ingress</div>
                    <p className="text-sm leading-relaxed">{highlights.hook}</p>
                  </div>
                )}
                {(highlights.points || []).length > 0 && (
                  <ul className="space-y-2">
                    {highlights.points.map((p, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span><span className="font-medium">{p.title}:</span> <span className="text-muted-foreground">{p.text}</span></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!hlLoading && !highlights && <p className="text-sm text-muted-foreground">Du kan ta fram blickfång när som helst inne i CV-editorn.</p>}
          </Card>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {result.apply_url ? (
              <a href={result.apply_url} target="_blank" rel="noopener noreferrer" data-testid="apply-to-employer-link" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="rounded-full w-full border-primary/40 text-primary hover:bg-primary/5">
                  <ExternalLink className="h-4 w-4 mr-2" /> Ansök hos arbetsgivaren
                </Button>
              </a>
            ) : <span className="hidden sm:block" />}
            <Button data-testid="open-cv-editor-btn" size="lg" className="rounded-full w-full sm:w-auto" onClick={() => navigate(`/app/cv/${result.id}`)}>
              Öppna i CV-editorn <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          {result.apply_url && (
            <p className="text-xs text-muted-foreground text-center sm:text-right -mt-2" data-testid="apply-hint">
              Vill du ansöka direkt? Ladda ner ditt CV i editorn och bifoga det på arbetsgivarens ansökningssida.
            </p>
          )}
        </div>
      )}

      {/* Complement dialog */}
      <Dialog open={!!comp} onOpenChange={(o) => !o && setComp(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Komplettera din profil</DialogTitle></DialogHeader>
          {comp && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{comp.question}</p>
              <div className="flex gap-2">
                {[["yes", "Ja"], ["no", "Nej"], ["unsure", "Osäker"]].map(([val, lbl]) => (
                  <Button key={val} data-testid={`comp-answer-${val}`} variant={compAnswer === val ? "default" : "outline"} className="rounded-full" onClick={() => setCompAnswer(val)}>{lbl}</Button>
                ))}
              </div>
              {compAnswer === "yes" && (
                <div className="space-y-2">
                  <Label>Beskriv din erfarenhet</Label>
                  <Textarea data-testid="comp-description" rows={4} value={compText} onChange={(e) => setCompText(e.target.value)} placeholder="Berätta kort om din erfarenhet av detta…" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setComp(null)}>Stäng</Button>
            <Button data-testid="save-complement-btn" className="rounded-full" onClick={saveComplement}>Spara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paywall dialog */}
      <Dialog open={!!paywall} onOpenChange={(o) => !o && setPaywall(null)}>
        <DialogContent data-testid="paywall-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Din gratiskvot är slut</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{paywall}</p>
            <SwishPricing audience="candidate" />
            <div className="space-y-2">
              <Label>Har du en accesskod?</Label>
              <div className="flex gap-2">
                <Input data-testid="redeem-code-input" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} placeholder="T.ex. HAMNFRI" />
                <Button data-testid="redeem-code-btn" className="rounded-full" onClick={redeem} disabled={redeeming}>
                  {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lös in"}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPaywall(null)}>Stäng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MatchCard({ title, icon: Icon, color, items, keyField, testid }) {
  return (
    <Card className="rounded-2xl border-border p-6" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-3 font-semibold ${color}`}><Icon className="h-5 w-5" /> {title}</div>
      <div className="space-y-2">
        {(items || []).length === 0 && <p className="text-sm text-muted-foreground">—</p>}
        {(items || []).map((m, i) => (
          <div key={i} className="text-sm">
            <span className="font-medium">{m.item}</span>
            {m[keyField] && <span className="text-muted-foreground"> — {m[keyField]}</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}
